import React, { useEffect, useState, useRef } from "react";
import Swal from "sweetalert2";
import moment from "moment";
import { auth, db } from "../firebase";
import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    getDoc,
    doc,
    Timestamp,
} from "firebase/firestore";
import { useReactToPrint } from "react-to-print";

import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    MenuItem,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
} from "@mui/material";

export default function SalePurchaseReport() {
    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
    });

    const printRef = useRef();

    /* ================= STATE ================= */
    const [propertyId, setPropertyId] = useState("");
    const [type, setType] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [custType, setCustType] = useState("");

    const [reportData, setReportData] = useState([]);

    const [totalAmount, setTotalAmount] = useState(0);
    const [paidAmount, setPaidAmount] = useState(0);
    const [discount, setDiscount] = useState(0);
    const [remaining, setRemaining] = useState(0);

    /* ================= PRINT ================= */
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
    });

    /* ================= AUTH → PROPERTY ================= */
    useEffect(() => {
        const unsub = auth.onAuthStateChanged(async (user) => {
            if (!user) return;

            const snap = await getDoc(doc(db, "authroles", user.uid));
            if (snap.exists()) {
                setPropertyId(snap.data().propertyId);
            }
        });

        return () => unsub();
    }, []);

    /* ================= DEFAULT DATES ================= */
    useEffect(() => {
        const today = moment().format("YYYY-MM-DD");
        setFromDate(today);
        setToDate(today);
    }, []);

    /* ================= FETCH REPORT ================= */
    const getReport = async () => {
        if (!type) {
            Toast.fire({ icon: "error", title: "Select report type" });
            return;
        }

        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);

        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);

        const fromTS = Timestamp.fromDate(from);
        const toTS = Timestamp.fromDate(to);



        setReportData([]);
        setTotalAmount(0);
        setPaidAmount(0);
        setDiscount(0);
        setRemaining(0);

        let q;
        let rows = [];

        let tTotal = 0;
        let tPaid = 0;
        let tDiscount = 0;
        let tRemaining = 0;

        if (type === "Sale") {
            setCustType("Customer");

            q = query(
                collection(db, "orders"),
                where("propertyId", "==", propertyId),
                where("billdate", ">=", fromTS),
                where("billdate", "<=", toTS),
                orderBy("billdate", "desc")
            );

            onSnapshot(q, async (snap) => {
                rows = [];

                for (const d of snap.docs) {
                    let name = "";

                    if (d.data().customerid) {
                        const cs = await getDoc(doc(db, "restocustomers", d.data().customerid));
                        name = cs.exists() ? cs.data().name : "";
                    }

                    tTotal += Number(d.data().totalamount || 0);
                    tPaid += Number(d.data().paid || 0);
                    tDiscount += Number(d.data().discount || 0);
                    tRemaining += Number(d.data().remaining || 0);

                    rows.push({
                        id: d.id,
                        date: d.data().billdate.toDate(), // normalize here
                        name,
                        total: d.data().totalamount,
                        paid: d.data().paid,
                        discount: d.data().discount,
                        remaining: d.data().remaining,
                    });
                }

                setReportData(rows);
                setTotalAmount(tTotal);
                setPaidAmount(tPaid);
                setDiscount(tDiscount);
                setRemaining(tRemaining);
            });
        } else {
            setCustType("Vendor");

            q = query(
                collection(db, "purchases"),
                where("propertyId", "==", propertyId),
                where("purchaseDate", ">=", fromDate),
                where("purchaseDate", "<=", toDate),
                orderBy("purchaseDate", "desc")
            );

            onSnapshot(q, async (snap) => {
                rows = [];

                for (const d of snap.docs) {
                    let name = "";

                    if (d.data().dealerId) {
                        const ds = await getDoc(doc(db, "dealers", d.data().dealerId));
                        name = ds.exists() ? ds.data().name : "";
                    }

                    tTotal += Number(d.data().grandtotal || 0);
                    tPaid += Number(d.data().paidamount || 0);
                    tRemaining += Number(d.data().remaining || 0);

                    rows.push({
                        id: d.id,
                        date: d.data().purchaseDate,
                        name,
                        total: d.data().grandtotal,
                        paid: d.data().paidamount,
                        discount: 0,
                        remaining: d.data().remaining,
                    });
                }

                setReportData(rows);
                setTotalAmount(tTotal);
                setPaidAmount(tPaid);
                setDiscount(0);
                setRemaining(tRemaining);
            });
        }
    };

    /* ================= UI ================= */
    return (
        <Box className=" p-3">
            <Card>
                <CardContent>
                    <Typography variant="h5" mb={2}>
                        Sale / Purchase Report
                    </Typography>

                    {/* FILTERS */}
                    <Grid className="row" spacing={2} mb={2}>
                        <Grid className="col-sm-3">
                            <TextField
                                select
                                fullWidth size="small"
                                label="Report Type"
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                            >
                                <MenuItem value="">Select</MenuItem>
                                <MenuItem value="Sale">Sale</MenuItem>
                                <MenuItem value="Purchase">Purchase</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid className="col-sm-3">
                            <TextField
                                fullWidth size="small"
                                type="date"
                                label="From Date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid className="col-sm-3">
                            <TextField
                                fullWidth size="small"
                                type="date"
                                label="To Date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid className="col-sm-3" alignSelf="center">
                            <Button variant="contained" fullWidth onClick={getReport}>
                                Get Report
                            </Button>
                        </Grid>
                    </Grid>

                    {/* TABLE */}
                    <TableContainer component={Paper} ref={printRef}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {[
                                        "#",
                                        "Date",
                                        `${custType} Name`,
                                        "Total",
                                        "Paid",
                                        "Discount",
                                        "Remaining",
                                    ].map((h) => (
                                        <TableCell key={h} sx={{ fontWeight: "bold" }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {reportData.map((r, i) => (
                                    <TableRow key={r.id}>
                                        <TableCell>{i + 1}</TableCell>
                                        <TableCell>
                                            {r.date?.toDate
                                                ? moment(r.date.toDate()).format("DD-MM-YYYY")   // Firestore Timestamp
                                                : moment(r.date).format("DD-MM-YYYY")}
                                        </TableCell>

                                        <TableCell>{r.name}</TableCell>
                                        <TableCell>{r.total}</TableCell>
                                        <TableCell>{r.paid}</TableCell>
                                        <TableCell>{r.discount}</TableCell>
                                        <TableCell>{r.remaining}</TableCell>
                                    </TableRow>
                                ))}

                                {/* TOTAL */}
                                <TableRow>
                                    <TableCell colSpan={3}>
                                        <b>Total</b>
                                    </TableCell>
                                    <TableCell>
                                        <b>{totalAmount}</b>
                                    </TableCell>
                                    <TableCell>
                                        <b>{paidAmount}</b>
                                    </TableCell>
                                    <TableCell>
                                        <b>{discount}</b>
                                    </TableCell>
                                    <TableCell>
                                        <b>{remaining}</b>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box textAlign="center" mt={2}>
                        <Button variant="outlined" onClick={handlePrint}>
                            Print Report
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
